import React, { useEffect } from "react";
import BookmarkDetailPageSidebar from "./bookmark-detail-page-sidebar";
import { Icons } from "atoms/constants/icons";
import useBookmarksByCollectionQuery from "utilities/hooks/queries/my-link/bookmarks/sidebars/use-bookmarks-by-collection-query";
import Button from "atoms/buttons/button";
import { t } from "utilities/localization-utils";
import { ButtonStyles } from "atoms/constants/button-styles";
import useModalActions from "utilities/hooks/use-modal-actions";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import usePagedResultsFromParams from "utilities/hooks/use-paged-results-from-params";
import useSearchBookmarks from "utilities/hooks/domain/my-link/use-search-bookmarks";
import useSortBookmarks from "utilities/hooks/domain/my-link/use-sort-bookmarks";
import SortBy from "../../options/sort-by";
import SearchBookmarks from "../../options/search-bookmarks";
import { useHistory, useParams } from "react-router";
import { BookmarkPaginationSettings } from "constants/bookmark-pagination-constants";
import { BookmarkUtils } from "utilities/bookmark-utils";
import { UserBookmarkCountBy } from "models/enumerations/sorting/user-bookmarks/user-bookmark-count-by";
import UpdateUserCollectionModal from "organisms/my-link/user-collections/update-user-collection-modal";
import UserCollectionRecord from "models/view-models/user-collection-record";
import useBookmarksAndCollections from "utilities/hooks/domain/my-link/use-bookmarks-and-collections";

const SIDEBAR_CLASS = "c-bookmark-detail-page-sidebar";

const SidebarForCollections = () => {
    const { groupId, id: bookmarkId } = useParams<{
        groupId: string;
        id: string;
    }>();
    const { push } = useHistory();
    const { sortBy, onSortByChange } = useSortBookmarks(
        UserBookmarkCountBy.Collection
    );
    const { searchText, onSearchTextChange } = useSearchBookmarks(
        BookmarkUtils.buildGroupedParam(UserBookmarkCountBy.Collection, groupId)
    );

    const { skip, take, onSetPage, currentPage, numberOfPages, setRowCount } =
        usePagedResultsFromParams(
            BookmarkPaginationSettings.SidebarTake,
            BookmarkUtils.buildGroupedParam(
                UserBookmarkCountBy.Collection,
                groupId
            )
        );
    const { bookmarks, collection, refetchCollection } =
        useBookmarksByCollectionQuery({
            skip,
            take,
            searchText,
            sortBy,
        });

    // Exposing refresh all so that detail page collection list is refreshed on name change
    const { refetchAll: refetchAllCollectionPillSOnDetailPage } =
        useBookmarksAndCollections(Number(bookmarkId));

    const { currentIdentity } = useCurrentIdentity();
    const {
        isOpen: manageCollectionModalIsOpen,
        handleOpen: handleOpenManageCollectionModal,
        handleClose: handleCloseManageCollectionModal,
    } = useModalActions();

    const canManage = collection?.userId === currentIdentity?.userId();

    const handleOnUserCollectionSave = () => {
        refetchCollection();
        refetchAllCollectionPillSOnDetailPage();
        handleCloseManageCollectionModal();
    };

    const handleDeleteCollectionRedirect = () => {
        push(BookmarkUtils.getViewByRoute(UserBookmarkCountBy.Collection));
    };

    useEffect(() => {
        if (bookmarks?.bookmarksLength) {
            setRowCount(bookmarks?.bookmarksLength);
        }
    }, [bookmarks?.bookmarksLength, setRowCount]);
    return (
        <>
            <BookmarkDetailPageSidebar
                totalPages={numberOfPages}
                onSetPage={onSetPage}
                currentPage={currentPage}
                bookmarks={bookmarks?.bookmarks}
                headingText={collection?.name ?? ""}
                headingIcon={Icons.Folder}
                additionalHeadingContent={
                    canManage ? (
                        <Button
                            cssClassName={`${SIDEBAR_CLASS}__sidebarAction`}
                            style={ButtonStyles.AnchorBlue}
                            onClick={handleOpenManageCollectionModal}>
                            {t("manageCollection")}
                        </Button>
                    ) : (
                        <></>
                    )
                }
                dropdowns={
                    <>
                        <SortBy sort={sortBy} onChange={onSortByChange} />
                        <SearchBookmarks
                            searchText={searchText}
                            onChange={onSearchTextChange}
                        />
                    </>
                }
            />
            <UpdateUserCollectionModal
                closeDialog={handleCloseManageCollectionModal}
                isVisible={manageCollectionModalIsOpen}
                userCollection={collection ?? new UserCollectionRecord()}
                onConfirm={handleOnUserCollectionSave}
                onDelete={handleDeleteCollectionRedirect}
            />
        </>
    );
};
export default SidebarForCollections;
